import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Home } from 'lucide-react';

const RECAPTCHA_SITE_KEY = '6LcVLIYsAAAAADaw3VMLLs4jXulXVwzwsp3qdex6';
export const REFERRAL_STORAGE_KEY = 'ref_code';
const REFERRAL_CLICK_TRACKED_KEY = 'ref_click_tracked';

export const ReferralLanding: React.FC = () => {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!code) return;
    const normalizedCode = code.toUpperCase();
    localStorage.setItem(REFERRAL_STORAGE_KEY, normalizedCode);

    // Dedup must survive remounts (reload, back/forward nav), not just this mount —
    // each click event costs a billed reCAPTCHA Enterprise assessment + CF invocation.
    if (localStorage.getItem(REFERRAL_CLICK_TRACKED_KEY) === normalizedCode) return;
    localStorage.setItem(REFERRAL_CLICK_TRACKED_KEY, normalizedCode);

    // Fire click tracking — non-blocking, failure never blocks signup
    (async () => {
      try {
        await new Promise<void>(resolve => window.grecaptcha.enterprise.ready(resolve));
        const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'referral_click' });
        const fn = httpsCallable(getFunctions(getApp()), 'trackReferral');
        await fn({ code: normalizedCode, event: 'click', recaptchaToken: token });
      } catch {
        // Non-fatal — tracking failure must never block the user
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950 p-6">
      <div className="flex flex-col items-center gap-6 max-w-sm text-center">
        <div className="p-3 rounded-2xl bg-brand text-white shadow-xl">
          <Home className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-950 dark:text-slate-100 tracking-tight">
            {t('referral.invited_to', "You're invited to")}{' '}
            <span className="text-brand">EverydayOS</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-3 text-sm leading-relaxed">
            {t('referral.description', 'The operating system for your household — tasks, groceries, calendar, and more, built for the way families actually live.')}
          </p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="w-full bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs px-6 py-4 rounded-2xl shadow-lg shadow-brand/20 transition-colors"
        >
          {t('referral.create_account', 'Create Account →')}
        </button>
        <button
          onClick={() => navigate('/login')}
          className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          {t('referral.sign_in', 'Already have an account? Sign in')}
        </button>
      </div>
    </div>
  );
};
